import { useState, useCallback, useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';
import type { QueueTab } from '../types';

// Re-export type for use in other components
export type { QueueTab };

// Query configuration constants (extracted from magic numbers)
export const QUERY_CONFIG = {
  ORDERS_STALE_TIME: 60_000, // 60 seconds - increased for instant tab switching
  ORDERS_GC_TIME: 5 * 60 * 1000, // 5 minutes garbage collection
  COUNTS_STALE_TIME: 2 * 60 * 1000, // 2 minutes - counts are expensive
  COUNTS_GC_TIME: 10 * 60 * 1000, // 10 minutes garbage collection
  FILTER_OPTIONS_STALE_TIME: 2 * 60 * 1000, // 2 minutes - options rarely change
  DEFAULT_PAGE_SIZE: 30,
} as const;

export type SortOrder = 'newest' | 'oldest';

/**
 * Server-side filter parameters for the order queue API.
 * Matches the Zod schema in production-orders router.
 */
export interface OrderQueueApiFilters {
  search?: string;
  ageRange?: 'today' | '1-3days' | '3+days';
  orderType?: 'single' | 'multi';
  tags?: string[];
  colors?: string[];
  colorsExclusive?: boolean;
  sizes?: string[];
  sizesExclusive?: boolean;
  productTypes?: string[];
  fulfillmentType?: 'pod_only' | 'pap_only' | 'mixed' | 'pod_dtg' | 'pod_dtf';
  /**
   * BLU-367/BLU-787: narrow the queue to one client org at a shared 3PL
   * location. Applied server-side as a SQL WHERE clause ANDed under the
   * caller's accessible-org scope — it can only ever narrow, never widen.
   */
  clientOrganizationId?: string;
}

/**
 * Fetch orders for the queue with caching.
 *
 * PERFORMANCE OPTIMIZED for instant tab switching:
 * - staleTime: 60s (increased from 30s) - data stays fresh longer
 * - refetchOnWindowFocus: false - prevents refetch on tab switch (KEY FIX)
 * - placeholderData: shows cached data instantly while background fetch happens
 * - gcTime (garbage collection): 5 minutes to keep cached data longer
 *
 * Filters become part of the tRPC query key automatically (tRPC serializes input),
 * so each filter combination gets its own cache entry.
 */
export function useOrderQueue(
  locationId: string | undefined,
  tab: QueueTab,
  options?: { limit?: number; offset?: number; sortOrder?: SortOrder; filters?: OrderQueueApiFilters }
) {
  return trpc.productionOrders.getOrderQueue.useQuery(
    {
      locationId: locationId!,
      tab,
      limit: options?.limit ?? QUERY_CONFIG.DEFAULT_PAGE_SIZE,
      offset: options?.offset ?? 0,
      // BLU-787: matches `DEFAULT_FILTERS.sortOrder`. OrdersPage always passes
      // this explicitly, so the fallback is only reachable by a future caller —
      // which is exactly why it must not disagree with the page default.
      sortOrder: options?.sortOrder ?? 'oldest',
      filters: options?.filters,
    },
    {
      enabled: !!locationId,
      staleTime: QUERY_CONFIG.ORDERS_STALE_TIME,
      gcTime: QUERY_CONFIG.ORDERS_GC_TIME,
      refetchOnWindowFocus: false,
      placeholderData: (previousData) => previousData
    }
  );
}

/**
 * Hook for paginated order queue with server-side filters.
 *
 * Supports two pagination modes:
 * - 'endless': Load More button pattern (original behavior)
 * - 'paginated': Traditional page navigation with Previous/Next
 *
 * Uses server-provided `hasMore` indicator for reliable pagination
 * (fixes heuristic that failed when eligibility filtering reduced results)
 *
 * Resets to page 1 when filters, sort order, tab, or pageSize change.
 */
export function usePaginatedOrderQueue(
  locationId: string | undefined,
  tab: QueueTab,
  pageSize: number = QUERY_CONFIG.DEFAULT_PAGE_SIZE,
  filters?: OrderQueueApiFilters,
  sortOrder?: SortOrder,
) {
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  // Accumulated orders for endless scroll mode
  const [accumulatedOrders, setAccumulatedOrders] = useState<Array<{ id: string; [key: string]: unknown }>>([]);

  // Track serialized filters, sort order, and tab for reset detection
  const filtersJson = JSON.stringify(filters);
  const prevFiltersRef = useRef(filtersJson);
  const prevSortOrderRef = useRef(sortOrder);
  // The tab (queueStatus) is passed as a separate arg and is intentionally NOT part of
  // `filters`/`apiFilters`, so a Ready<->All switch would otherwise leave a stale offset:
  // a large offset carried from a big tab slices out of bounds on a smaller tab, yielding
  // 0 rows while `total` still reports the real count ("Showing 0 of 17 / No orders found").
  const prevTabRef = useRef(tab);

  // Reset to first page when filters, sort order, or tab change
  useEffect(() => {
    const filtersChanged = prevFiltersRef.current !== filtersJson;
    const sortChanged = prevSortOrderRef.current !== sortOrder;
    const tabChanged = prevTabRef.current !== tab;
    if (filtersChanged || sortChanged || tabChanged) {
      prevFiltersRef.current = filtersJson;
      prevSortOrderRef.current = sortOrder;
      prevTabRef.current = tab;
      setOffset(0);
      setCurrentPage(0);
      setAccumulatedOrders([]);
    }
  }, [filtersJson, sortOrder, tab]);

  // Reset when pageSize changes
  useEffect(() => {
    setOffset(0);
    setCurrentPage(0);
    setAccumulatedOrders([]);
  }, [pageSize]);

  const query = useOrderQueue(locationId, tab, { limit: pageSize, offset, sortOrder, filters });

  // Accumulate orders for endless scroll when new data arrives
  // Deduplicates by order ID to handle overlapping DB ranges from over-fetch multiplier
  // NOTE: No offset ref guard - placeholderData serves stale data on offset change,
  // then real data arrives with same offset. Dedup handles both cases safely.
  useEffect(() => {
    if (query.data?.orders) {
      if (offset === 0) {
        // First page or reset - replace
        setAccumulatedOrders(query.data.orders);
      } else {
        // Append new page, deduplicating by ID
        setAccumulatedOrders(prev => {
          const existingIds = new Set(prev.map(o => o.id));
          const newOrders = query.data.orders.filter(o => !existingIds.has(o.id));
          if (newOrders.length === 0) return prev; // No change - skip re-render
          return [...prev, ...newOrders];
        });
      }
    }
  }, [query.data?.orders, offset]);

  // Load More (endless mode): accumulate offset
  const loadMore = useCallback(() => {
    setOffset(prev => prev + pageSize);
  }, [pageSize]);

  // Go to specific page (paginated mode): jump to page offset with bounds checking
  const goToPage = useCallback((page: number) => {
    const safePage = Math.max(0, page);
    setCurrentPage(safePage);
    setOffset(safePage * pageSize);
  }, [pageSize]);

  const reset = useCallback(() => {
    setOffset(0);
    setCurrentPage(0);
    setAccumulatedOrders([]);
  }, []);

  // Use server-provided hasMore indicator (reliable even with eligibility filtering)
  const hasMore = query.data?.hasMore ?? false;
  const total = query.data?.total ?? 0;

  return {
    ...query,
    // Unwrap orders from response for backwards compatibility
    data: query.data?.orders,
    // Accumulated orders for endless scroll mode
    accumulatedOrders,
    // Pagination helpers
    loadMore,
    goToPage,
    reset,
    hasMore,
    total,
    currentOffset: offset,
    currentPage,
    totalPages: Math.ceil(total / pageSize),
    pageSize,
  };
}

/**
 * Fetch available filter options (colors, sizes, product types, tags)
 * for the order queue dropdowns. Static (not cascading).
 */
export function useFilterOptions(locationId: string | undefined) {
  return trpc.productionOrders.getFilterOptions.useQuery(
    { locationId: locationId! },
    {
      enabled: !!locationId,
      staleTime: QUERY_CONFIG.FILTER_OPTIONS_STALE_TIME,
      gcTime: QUERY_CONFIG.ORDERS_GC_TIME,
      refetchOnWindowFocus: false,
    }
  );
}

/**
 * Fetch tab counts with more aggressive caching.
 *
 * PERFORMANCE FIX: Counts are expensive (backend computes eligibility for all orders).
 * Use longer staleTime to reduce server load.
 */
export function useOrderQueueCounts(locationId: string | undefined) {
  return trpc.productionOrders.getQueueCounts.useQuery(
    { locationId: locationId! },
    {
      enabled: !!locationId,
      staleTime: QUERY_CONFIG.COUNTS_STALE_TIME,
      gcTime: QUERY_CONFIG.COUNTS_GC_TIME,
      refetchOnWindowFocus: false // Don't refetch on every focus (expensive)
    }
  );
}

export function useOrder(orderId: string | undefined) {
  return trpc.productionOrders.getOrder.useQuery(
    { orderId: orderId! },
    {
      enabled: !!orderId,
      staleTime: 30_000
    }
  );
}
